import { Button, ColumnTable, Icon, Loading, Table } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { SectionTitle } from '../../../../components/text';
import { TranslationsKeys } from '../../../../locales/translations';
import { formatDate } from '../../../../utils/dates';
import { StudentRiskEvaluationSessionEnrollment } from '../../types';
import { UpdateSessionModal } from '../../views/studentSessionDetail/parts/UpdateSessionModal';
import RiskLevelBadge from '../RiskLevelBadge';
import { BadgeStatus } from './BadgeStatus';
import { useStudentsRiskHistoryDetails } from './loaders/StudentsRiskHistoryDetailsLoader';
import { StudentsRiskHistoryDetailsOrigin } from './types';

type StudentsRiskHistoryDetailsHistoryTableProps = {
  origin?: StudentsRiskHistoryDetailsOrigin;
};

const StudentsRiskHistoryDetailsHistoryTable = ({
  origin = 'riskSessionHistory',
}: StudentsRiskHistoryDetailsHistoryTableProps) => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_WARNINGS_STUDENT_HISTORY,
  );

  const [isUpdateSessionModalOpen, setIsUpdateSessionModalOpen] = useState(
    false,
  );
  const [
    focusedSessionModalOpen,
    setFocusedSessionModalOpen,
  ] = useState<StudentRiskEvaluationSessionEnrollment>();

  const { loading, result, refresh } = useStudentsRiskHistoryDetails();

  const lastSessionEnrollment = useMemo(() => {
    if (!result?.success || !result?.data) {
      return null;
    }
    return result.data?.history[0] || null;
  }, [result]);

  const texts = useMemo(() => {
    return {
      title: t('studentHistory.title'),
      currentStatus: t('studentHistory.currentStatus'),
      updateStatus: t('studentHistory.updateStatus'),
      noRecords: t('studentHistory.table.noRecords'),
      noRecordsSubtitle: t('studentHistory.table.noRecordsSubtitle'),
      date: t('studentHistory.table.columns.date'),
      observation: t('studentHistory.table.columns.observation'),
      situation: t('studentHistory.table.columns.situation'),
      continuesInCEIA: t('studentHistory.table.columns.continuesInCEIA'),
      calculatedRiskLevel: t(
        'studentHistory.table.columns.calculatedRiskLevel',
      ),
      recategorizedRiskLevel: t(
        'studentHistory.table.columns.recategorizedRiskLevel',
      ),
      conditionality: t('studentHistory.table.columns.conditionality'),
      details: t('studentHistory.table.columns.details'),
      yes: t('studentHistory.table.yes'),
      no: t('studentHistory.table.no'),
    };
  }, [t]);

  const openStateModal = useCallback(
    (sessionEnrollment?: StudentRiskEvaluationSessionEnrollment) => {
      setFocusedSessionModalOpen(sessionEnrollment);
      setIsUpdateSessionModalOpen(true);
    },
    [],
  );
  const closeStateModal = useCallback(() => {
    setFocusedSessionModalOpen(undefined);
    setIsUpdateSessionModalOpen(false);
  }, []);

  const columns = useMemo(() => {
    const cols: (ColumnTable<StudentRiskEvaluationSessionEnrollment> & {
      hidden?: boolean;
    })[] = [
      {
        headerText: texts.date,
        columnName: 'date',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return row.session?.date
            ? formatDate(row.session.date, 'DD/MM/YYYY')
            : '-';
        },
      },
      {
        headerText: texts.observation,
        columnName: 'notes',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.situation,
        columnName: 'state',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return <BadgeStatus active={row.state} />;
        },
      },
      {
        headerText: texts.continuesInCEIA,
        columnName: 'keepForNextSession',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value ? texts.yes : texts.no;
        },
      },
      {
        headerText: texts.calculatedRiskLevel,
        columnName: 'calculatedRiskLevel',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) =>
          value ? <RiskLevelBadge riskLevel={value} /> : '-',
      },
      {
        headerText: texts.recategorizedRiskLevel,
        columnName: 'definedRiskLevel',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) =>
          value ? <RiskLevelBadge riskLevel={value} /> : '-',
      },
      {
        headerText: texts.conditionality,
        columnName: 'conditionality',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value ? texts.yes : texts.no;
        },
      },
      {
        headerText: texts.details,
        hidden: origin === 'studentFile',
        columnName: 'id',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          return (
            <button
              type="button"
              className="btn"
              onClick={() => openStateModal(row)}
            >
              <Icon name="eye" />
            </button>
          );
        },
      },
    ];
    return cols.filter((col) => !col.hidden);
  }, [texts, openStateModal, origin]);

  if (loading) {
    return (
      <>
        <Loading textBody={''} />
      </>
    );
  }

  if (!result?.success) {
    return <></>;
  }

  return (
    <>
      <div className="w-100 mt-4 d-block">
        <Row className="d-flex justify-content-between align-items-center mt-4">
          <Col xs={12} md={6}>
            <SectionTitle text={texts.title} />
          </Col>
          {origin !== 'studentFile' && (
            <Col
              xs={12}
              md={6}
              className="d-flex flex-row align-items-center justify-content-end"
              style={{ gap: 12 }}
            >
              <div className="d-flex flex-row align-items-center justify-content-end">
                <span>{texts.currentStatus}</span>
                <BadgeStatus active={lastSessionEnrollment?.state} />
              </div>
              {origin === 'riskSession' && (
                <div style={{ maxWidth: 275 }}>
                  <Button
                    text={texts.updateStatus}
                    size="md"
                    outlined
                    fullwidth
                    onClick={() => openStateModal()}
                    type="button"
                  />
                </div>
              )}
            </Col>
          )}
          <Col className="mt-4" xs={12}>
            <Table
              data={result?.data?.history ?? []}
              columns={columns}
              isLoadingResults={loading}
            />
          </Col>
        </Row>
      </div>
      {isUpdateSessionModalOpen && (
        <UpdateSessionModal
          onClose={closeStateModal}
          options={result?.data?.formOptions}
          onSuccess={refresh}
          isReadOnly={!!focusedSessionModalOpen}
          sessionEnrollment={
            focusedSessionModalOpen || lastSessionEnrollment || undefined
          }
        />
      )}
    </>
  );
};

export default StudentsRiskHistoryDetailsHistoryTable;
